import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Link } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ESPWebTools from '../ESPWebTools';

// Shown when the sensor is plugged in but never completes the HELLO handshake.
// The usual "update your sensor" wizard also relies on that handshake, so a
// sensor running firmware older than the serial command protocol (e.g. 0.0.6,
// which is BLE-only and never answers HELLO) can't be reached that way. The
// ESP Web Tools installer flashes over USB via the ESP32 ROM bootloader, which
// needs no running firmware, so it is the one recovery path that always works.

const COPY = {
  'port-busy': {
    title: 'The sensor port is in use',
    body: 'Another program or browser tab is using the sensor. Close other tabs, the Arduino IDE, or any serial monitor, then Retry.',
  },
  'open-failed': {
    title: "We couldn't open the sensor",
    body: 'On Windows you may need a one-time USB driver. Install it, replug the sensor, then Retry.',
  },
  'silent': {
    title: "Couldn't connect to your sensor",
    body: "The device didn't respond. Check your USB cable and try again.",
  },
  'transient': {
    title: 'Connection dropped',
    body: 'The sensor disconnected mid-handshake. Replug it and Retry.',
  },
};

export default function ConnectFailedPanel({
  onRetry,
  onOpenWizard,
  manifestPath,
  serialSupported,
  reason = 'silent',
}) {
  const copy = COPY[reason] || COPY.silent;
  const showReinstall = serialSupported && reason !== 'port-busy' && reason !== 'transient';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        textAlign: 'center',
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        {copy.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
        {copy.body}
      </Typography>
      <Button variant="contained" onClick={onRetry} sx={{ mb: 2 }}>
        Retry
      </Button>
      <Link component="button" variant="body2" onClick={onOpenWizard} sx={{ mb: 3 }}>
        Running older firmware? Update your sensor
      </Link>

      {showReinstall && (
        <Box sx={{ maxWidth: 480, width: '100%' }}>
          <ESPWebTools
            manifestPath={manifestPath}
            title="Sensor still won't connect?"
            description={
              'Sensors on very old firmware can\'t talk to this page. Reinstall the ' +
              'latest firmware directly over USB — no connection needed. This erases ' +
              'the sensor and installs a fresh build.'
            }
          />
        </Box>
      )}
    </Box>
  );
}

ConnectFailedPanel.propTypes = {
  onRetry: PropTypes.func.isRequired,
  onOpenWizard: PropTypes.func.isRequired,
  manifestPath: PropTypes.string.isRequired,
  serialSupported: PropTypes.bool.isRequired,
  reason: PropTypes.string,
};
